const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

const humanizeField = (field) =>
  String(field)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const appendFormattedValue = (parts, value, prefix = "") => {
  if (value == null || value === "") {
    return;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    parts.push(prefix ? `${prefix}: ${value}` : String(value));
    return;
  }

  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === "string")) {
      const text = value.join(", ");
      parts.push(prefix ? `${prefix}: ${text}` : text);
      return;
    }

    value.forEach((item, index) => {
      const rowPrefix = prefix || `Row ${index + 1}`;
      appendFormattedValue(parts, item, rowPrefix);
    });
    return;
  }

  if (typeof value === "object") {
    Object.entries(value).forEach(([key, nestedValue]) => {
      const label = humanizeField(key);
      appendFormattedValue(parts, nestedValue, prefix ? `${prefix} - ${label}` : label);
    });
  }
};

export const formatErrorPayload = (data, fallback = DEFAULT_ERROR_MESSAGE) => {
  if (!data) {
    return fallback;
  }

  if (typeof data === "string") {
    return data;
  }

  const parts = [];

  appendFormattedValue(parts, data.message);
  appendFormattedValue(parts, data.detail);

  if (Array.isArray(data.errors) && data.errors.length) {
    data.errors.forEach((errorMessage) => appendFormattedValue(parts, errorMessage));
  }

  if (data.invalid_categories?.length) {
    parts.push(`Invalid categories: ${data.invalid_categories.join(", ")}`);
  }

  if (data.duplicate_names?.length) {
    parts.push(`Duplicate names: ${data.duplicate_names.join(", ")}`);
  }

  if (data.details?.missing_columns?.length) {
    parts.push(`Missing columns: ${data.details.missing_columns.join(", ")}`);
  }

  if (data.details?.extra_columns?.length) {
    parts.push(`Extra columns: ${data.details.extra_columns.join(", ")}`);
  }

  /*
   * Everything above reads the shapes this product's own endpoints send. A
   * DRF serializer refuses in its own shape and none of them match it: a bare
   * list of strings, `non_field_errors`, or one key per field that was wrong -
   * `{"items": [{"quantity": ["Cannot return more than billed"]}]}`. Those
   * used to fall through to "Something went wrong", which threw away the one
   * sentence that said what to do about it.
   *
   * Read only when nothing else matched, so no message that already works is
   * rewritten by it.
   */
  if (!parts.length) {
    appendFormattedValue(parts, data.non_field_errors);
  }

  if (!parts.length && Array.isArray(data)) {
    appendFormattedValue(parts, data);
  }

  if (!parts.length && typeof data === "object") {
    /* Keys already read above, and the ones that carry no complaint. */
    const read = new Set([
      "message",
      "detail",
      "errors",
      "non_field_errors",
      "invalid_categories",
      "duplicate_names",
      "details",
      "status",
      "status_code",
      "code",
      "success",
    ]);
    Object.entries(data).forEach(([key, value]) => {
      if (read.has(key)) return;
      appendFormattedValue(parts, value, humanizeField(key));
    });
  }

  const uniqueParts = [...new Set(parts.filter(Boolean))];
  return uniqueParts.join("\n") || fallback;
};

/**
 * Extracts a user-friendly error message from various error shapes.
 * @param {Error|Object} error - Axios error, network error, or unknown error
 * @returns {string} User-facing error message
 */
export const getErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (responseData) {
    const formatted = formatErrorPayload(responseData);
    if (formatted !== DEFAULT_ERROR_MESSAGE) {
      return formatted;
    }
  }

  // Network error (no response received)
  if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
    return "Unable to connect to the server. Please check your internet connection.";
  }

  // Timeout
  if (error?.code === "ECONNABORTED") {
    return "The request timed out. Please try again.";
  }

  // HTTP status-based fallbacks
  if (error?.response?.status) {
    const status = error.response.status;
    if (status === 403) return "You don't have permission to perform this action.";
    if (status === 404) return "The requested resource was not found.";
    if (status === 429) return "Too many requests. Please wait and try again.";
    if (status >= 500) return "Something went wrong on our end. Please try again later.";
  }

  if (typeof error?.message === "string" && error.message) {
    return error.message;
  }

  return DEFAULT_ERROR_MESSAGE;
};
